
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { useFormContext } from "react-hook-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export function InvoiceSettingsTab() {
  const form = useFormContext();
  const isMobile = useIsMobile();

  return (
    <Card className={isMobile ? "shadow-sm border-0 rounded-none" : ""}>
      <CardHeader className={isMobile ? "px-4 pt-4 pb-2" : ""}>
        <CardTitle className={isMobile ? "text-xl" : ""}>Invoice Settings</CardTitle>
        <CardDescription className={isMobile ? "text-sm" : ""}>
          Configure invoice details for this project. These settings will be used when generating invoices.
        </CardDescription>
      </CardHeader>
      <CardContent className={`grid gap-4 ${isMobile ? "px-4 py-2" : "gap-6"}`}>
        <Alert className={`mb-2 bg-muted/50 ${isMobile ? "p-3" : ""}`}>
          <Info className="h-4 w-4" />
          <AlertDescription className={isMobile ? "text-xs" : ""}>
            Fill in these details to have them automatically populate in your invoice exports.
          </AlertDescription>
        </Alert>
        
        <div className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2 gap-6"}`}>
          <FormField
            control={form.control}
            name="invoiceSettings.clientName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isMobile ? "text-sm" : ""}>Client Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter client name" 
                    className={isMobile ? "text-sm h-9" : ""}
                    {...field} 
                  />
                </FormControl>
                <FormDescription className={isMobile ? "text-xs" : ""}>
                  Company or individual name that will appear on invoices
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="invoiceSettings.clientEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isMobile ? "text-sm" : ""}>Client Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="Enter client email" 
                    className={isMobile ? "text-sm h-9" : ""}
                    {...field} 
                  />
                </FormControl>
                <FormDescription className={isMobile ? "text-xs" : ""}>
                  Email for invoice delivery and communication
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="invoiceSettings.clientAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={isMobile ? "text-sm" : ""}>Client Address</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter client address"
                  className={`${isMobile ? "text-sm min-h-[60px]" : "min-h-[80px]"}`}
                  {...field}
                />
              </FormControl>
              <FormDescription className={isMobile ? "text-xs" : ""}>
                Complete business address for billing purposes
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2 gap-6"}`}>
          <FormField
            control={form.control}
            name="invoiceSettings.paymentTerms"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isMobile ? "text-sm" : ""}>Payment Terms</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g., Net 30" 
                    className={isMobile ? "text-sm h-9" : ""}
                    {...field} 
                  />
                </FormControl>
                <FormDescription className={isMobile ? "text-xs" : ""}>
                  When payment is due (e.g., "Net 30", "Due on receipt")
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="invoiceSettings.paymentMethods"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={isMobile ? "text-sm" : ""}>Payment Methods</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g., Bank Transfer, PayPal" 
                    className={isMobile ? "text-sm h-9" : ""}
                    {...field} 
                  />
                </FormControl>
                <FormDescription className={isMobile ? "text-xs" : ""}>
                  How clients can pay you
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="invoiceSettings.bankDetails"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={isMobile ? "text-sm" : ""}>Bank Details</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter bank account details"
                  className={`${isMobile ? "text-sm min-h-[60px]" : "min-h-[80px]"}`}
                  {...field}
                />
              </FormControl>
              <FormDescription className={isMobile ? "text-xs" : ""}>
                Account number, routing number, SWIFT code, etc.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="invoiceSettings.notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={isMobile ? "text-sm" : ""}>Additional Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter any additional notes for invoices"
                  className={`${isMobile ? "text-sm min-h-[60px]" : "min-h-[80px]"}`}
                  {...field}
                />
              </FormControl>
              <FormDescription className={isMobile ? "text-xs" : ""}>
                Any other information you want to include on all invoices
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
